import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy, Inject, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SseService } from 'src/app/services/SseService';
import { VentanaMinimizable } from 'src/app/utils/ventana-minimizable.util';
interface NumericParam {
  id: number;
  value: number;
  start: number;
  end: number;
  step: number;
  decimals: number;
  position: number;
}

@Component({
  selector: 'app-editar-parametros-condicion',
  templateUrl: './editar-parametros-condicion.component.html',
  styleUrls: ['./editar-parametros-condicion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditarParametrosCondicionComponent implements OnInit {
  // El @Input() ya no es necesario cuando abrimos vía MatDialog, pero lo dejamos por compatibilidad
  @Input() condition = '';
  @Input() isDark = false;
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<{
    template: string;
    params: Array<{ id: number; start: number; end: number; step: number; decimals: number; }>;
    combinations: number;
    stopLossRange?: { original: number; start: number; end: number; step: number };
    profitRange?: { original: number; start: number; end: number; step: number };
    diasSalidaRange?: { original: number; start: number; end: number; step: number };
  }>();

  cargado: boolean = false;
  conditionjson: any;
  form!: FormGroup;
  params: NumericParam[] = [];
  conditionTemplate = '';
  modelo: any;
  stoplossoriginal: any;
  profitoriginal: any;
  diassalidaoriginal: any;
  valorriesgooriginal: any;
  // Vista compacta para muchos parámetros
  viewCompact: boolean = true;
  // Índice del parámetro con foco para resaltar en la plantilla
  activeParamIndex: number | null = null;
  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<EditarParametrosCondicionComponent>, private _sseservice: SseService,
    private cdr: ChangeDetectorRef,
  ) {
    this.ventana = new VentanaMinimizable(this.dialogRef, !!this.data?.flotante, '600px');
  }

  /** Minimizar/restaurar cuando se abre como ventana flotante (data.flotante). */
  ventana: VentanaMinimizable;

  /** Con muchos parámetros la tabla se parte en dos columnas (y la ventana se ensancha). */
  static readonly MAX_PARAMS_UNA_COLUMNA = 4;
  dosColumnas = false;
  /** Secciones plegadas (clic en su título); se recuerda entre aperturas. */
  plegada: Record<string, boolean> = EditarParametrosCondicionComponent.leerPlegadas();
  private static readonly CLAVE_PLEGADAS = 'editarParametros_seccionesPlegadas';

  private static leerPlegadas(): Record<string, boolean> {
    try {
      return JSON.parse(localStorage.getItem(EditarParametrosCondicionComponent.CLAVE_PLEGADAS) || '{}') || {};
    } catch {
      return {};
    }
  }

  togglePlegar(seccion: string): void {
    this.plegada = { ...this.plegada, [seccion]: !this.plegada[seccion] };
    try { localStorage.setItem(EditarParametrosCondicionComponent.CLAVE_PLEGADAS, JSON.stringify(this.plegada)); } catch { }
  }

  /** Índices de parámetros de cada columna de la tabla compacta. */
  columnasParams: number[][] = [];

  private repartirColumnas(): void {
    const n = this.params.length;
    const indices = Array.from({ length: n }, (_, i) => i);
    this.dosColumnas = n > EditarParametrosCondicionComponent.MAX_PARAMS_UNA_COLUMNA;
    if (this.dosColumnas) {
      const mitad = Math.ceil(n / 2);
      this.columnasParams = [indices.slice(0, mitad), indices.slice(mitad)];
    } else {
      this.columnasParams = [indices];
    }
    this.ventana.setAnchoNormal(this.dosColumnas ? '1040px' : '600px');
  }

  ngOnInit(): void {
    // 1. Ver qué llega
    console.log('[EditarParametrosCondicion] data recibido:', this.data);

    // 2. Normalizar (en algoritmo se estaba enviando "result" completo)
    const raw = this.data?.condition;
    this.condition = this.normalizeCondition(raw);

    console.log('[EditarParametrosCondicion] condición normalizada:', this.condition);
    console.log(this.data);
    // 3. (Restante lógica original)
    this.params = this.extractNumericParams(this.condition);
    this.conditionTemplate = this.buildTemplate(this.condition, this.params);
    this.repartirColumnas();

    this.form = this.fb.group({
      parametros: this.fb.array(
        this.params.map(p =>
          this.fb.group({
            id: [p.id],
            original: [{ value: p.value, disabled: true }],
            start: [p.start, [Validators.required]],
            end: [p.end, [Validators.required]],
            step: [p.step, [Validators.required, Validators.min(p.decimals ? 0.01 : 1)]]
          })
        )
      ),
      // Rangos adicionales para Stop Loss y Profit
      stopLoss: this.fb.group({
        original: [{ value: 0, disabled: true }],
        start: [0, [Validators.required, Validators.min(0)]],
        end: [0, [Validators.required, Validators.min(0)]],
        step: [1, [Validators.required, Validators.min(0.0001)]]
      }),
      profit: this.fb.group({
        original: [{ value: 0, disabled: true }],
        start: [0, [Validators.required, Validators.min(0)]],
        end: [0, [Validators.required, Validators.min(0)]],
        step: [1, [Validators.required, Validators.min(0.0001)]]
      }),
      // Rango adicional para Días de salida
      diasSalida: this.fb.group({
        original: [{ value: 0, disabled: true }],
        start: [0, [Validators.required, Validators.min(0)]],
        end: [0, [Validators.required, Validators.min(0)]],
        step: [1, [Validators.required, Validators.min(1)]]
      })
      ,
      // Rango para el valor de riesgo (proporcionado desde la condición/modelo)
      riesgo: this.fb.group({
        original: [{ value: 0, disabled: true }],
        start: [0, [Validators.required, Validators.min(0)]],
        end: [0, [Validators.required, Validators.min(0)]],
        step: [10, [Validators.required, Validators.min(0.0001)]]
      })
    });
    console.log(this.data.condition);
    this.conditionjson = this._sseservice.apiGetNew('parametrosmodelo?idmodelo=' + this.data.condition.group + '&valorRiesgo=' + this.data.condition.valorriesgo).subscribe({
      next: (data: any) => {
        this.cargado = true;
        this.conditionjson = JSON.parse(data.condicionln).list_conditions_entry;
        //console.log(data);
        //this.modelo = JSON.parse(data);
        console.log(data.condicionln);
        this.stoplossoriginal = JSON.parse(data.condicionln).stopLoss;
        this.profitoriginal = JSON.parse(data.condicionln).profit;
        this.diassalidaoriginal = JSON.parse(data.condicionln).diasSalida;
        this.valorriesgooriginal = this.data.condition.valorriesgo;
        console.log(' ' + this.stoplossoriginal + ' ' + this.profitoriginal);
        //console.log(this.conditionjson);
        // Actualizar los valores originales y por defecto de los rangos si existen
        const sl = this.form.get('stopLoss');
        const pf = this.form.get('profit');
        const ds = this.form.get('diasSalida');
        if (sl && typeof this.stoplossoriginal === 'number') {
          const stepSL = this.computeStepFromValue(this.stoplossoriginal);
          sl.patchValue({
            original: this.stoplossoriginal,
            start: this.stoplossoriginal,
            end: this.stoplossoriginal,
            step: stepSL
          });
        }
        if (pf && typeof this.profitoriginal === 'number') {
          const stepPF = this.computeStepFromValue(this.profitoriginal);
          pf.patchValue({
            original: this.profitoriginal,
            start: this.profitoriginal,
            end: this.profitoriginal,
            step: stepPF
          });
        }
        if (ds && typeof this.diassalidaoriginal === 'number') {
          const stepDS = this.computeStepFromValue(this.diassalidaoriginal);
          ds.patchValue({
            original: this.diassalidaoriginal,
            start: this.diassalidaoriginal,
            end: this.diassalidaoriginal,
            step: stepDS || 1
          });
        }
        // Riesgo: valor provisto en data.condition.valorriesgo
        const r = this.form.get('riesgo');
        if (r && typeof this.valorriesgooriginal === 'number') {
          const stepR = this.computeStepFromValue(this.valorriesgooriginal);
          r.patchValue({
            original: this.valorriesgooriginal,
            start: this.valorriesgooriginal,
            end: this.valorriesgooriginal,
            step: stepR || 1
          });
        }
        // Ensure OnPush view updates after async patching
        try {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        } catch { }
      },
      error: (err: any) => {
        console.error('Error cargando parámetros del modelo:', err);
        this.cargado = false;
        try {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        } catch { }
      }
    });
  }

  private computeStepFromValue(value: number): number {
    if (value == null || isNaN(value)) return 1;
    const s = String(value);
    const dot = s.indexOf('.')
    if (dot === -1) return 1;
    const decimals = s.length - dot - 1;
    if (decimals <= 0) return 1;
    const step = Math.pow(10, -decimals);
    return parseFloat(step.toFixed(decimals));
  }

  get parametrosFA(): FormArray {
    return this.form.get('parametros') as FormArray;
  }

  private extractNumericParams(cond: string): NumericParam[] {
    const result: NumericParam[] = [];
    const numberRegex = /-?\d+(?:\.\d+)?/g;
    let match: RegExpExecArray | null;
    let id = 0;
    while ((match = numberRegex.exec(cond)) !== null) {
      const raw = match[0];
      const startIndex = match.index;
      const prev = cond[startIndex - 1];
      const next = cond[startIndex + raw.length];
      if ((prev && /[A-Za-z_]/.test(prev)) || (next && /[A-Za-z_]/.test(next))) continue;
      const valueNum = parseFloat(raw);
      const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
      const step = decimals > 0 ? parseFloat((1 / Math.pow(10, decimals)).toFixed(decimals)) : 1;
      result.push({
        id: id++,
        value: valueNum,
        start: valueNum,
        end: valueNum,
        step,
        decimals,
        position: startIndex
      });
    }
    return result;
  }

  private buildTemplate(cond: string, params: NumericParam[]): string {
    if (!params.length) return cond;
    let output = '';
    let cursor = 0;
    params.forEach(p => {
      output += cond.substring(cursor, p.position) + '{{p' + p.id + '}}';
      cursor = p.position + p.value.toString().length;
    });
    output += cond.substring(cursor);
    return output;
  }

  valueCount(ctrl: AbstractControl): number {
    const start = parseFloat(ctrl.get('start')!.value);
    const end = parseFloat(ctrl.get('end')!.value);
    const step = parseFloat(ctrl.get('step')!.value);
    if (isNaN(start) || isNaN(end) || isNaN(step) || step <= 0 || end < start) return 0;
    const raw = ((end - start) / step) + 1;
    return Math.floor(raw + 1e-9); // tolerancia flotante
  }

  combinations(): number {
    // Producto cartesiano de todos los parámetros + stopLoss + profit
    let product = 1;
    // Parámetros detectados en la condición
    if (this.parametrosFA?.controls?.length) {
      product = this.parametrosFA.controls.reduce((acc, c) => {
        const cnt = this.valueCount(c);
        return acc * (cnt || 0);
      }, product);
    }
    // StopLoss
    const sl = this.form.get('stopLoss');
    if (sl) {
      product *= this.valueCount(sl);
    }
    // Profit
    const pf = this.form.get('profit');
    if (pf) {
      product *= this.valueCount(pf);
    }
    // Días de salida
    const ds = this.form.get('diasSalida');
    if (ds) {
      product *= this.valueCount(ds);
    }
    return product;
  }

  restoreOriginal(idx: number): void {
    const g = this.parametrosFA.at(idx) as FormGroup;
    const original = this.params[idx].value;
    g.patchValue({ start: original, end: original });
  }

  private normalizeCondition(raw: any): string {
    if (!raw) return '';
    // Si ya es string
    if (typeof raw === 'string') return raw;
    // Si es un objeto con estructura { type, condition, conditions[] }
    if (raw.type === 'Condition' && raw.condition) return raw.condition;
    if (raw.type === 'Combination' && Array.isArray(raw.conditions)) {
      return raw.conditions.join(' AND ');
    }
    // Fallback: intentar serializar
    return JSON.stringify(raw);
  }

  onCancel(): void {
    this.dialogRef.close();
    this.cancel.emit();
  }

  onSave(): void {
    if (this.form.invalid) {
      console.log('[EditarParametrosCondicion] Formulario inválido');
      return;
    }

    const payloadParams = this.parametrosFA.controls.map(ctrl => ({
      id: ctrl.get('id')!.value,
      start: parseFloat(ctrl.get('start')!.value),
      end: parseFloat(ctrl.get('end')!.value),
      step: parseFloat(ctrl.get('step')!.value),
      original: parseFloat(ctrl.get('original')!.value),
      decimals: this.params.find(p => p.id === ctrl.get('id')!.value)!.decimals
    }));

    const payload = {
      template: this.conditionTemplate,
      params: payloadParams,
      combinations: this.combinations(),
      condicionjson: this.conditionjson,
      stopLossRange: {
        original: parseFloat((this.form.get('stopLoss.original') as any)?.value ?? this.stoplossoriginal ?? 0),
        start: parseFloat(this.form.get('stopLoss.start')!.value),
        end: parseFloat(this.form.get('stopLoss.end')!.value),
        step: parseFloat(this.form.get('stopLoss.step')!.value)
      },
      profitRange: {
        original: parseFloat((this.form.get('profit.original') as any)?.value ?? this.profitoriginal ?? 0),
        start: parseFloat(this.form.get('profit.start')!.value),
        end: parseFloat(this.form.get('profit.end')!.value),
        step: parseFloat(this.form.get('profit.step')!.value)
      },
      diasSalidaRange: {
        original: parseFloat((this.form.get('diasSalida.original') as any)?.value ?? this.diassalidaoriginal ?? 0),
        start: parseFloat(this.form.get('diasSalida.start')!.value),
        end: parseFloat(this.form.get('diasSalida.end')!.value),
        step: parseFloat(this.form.get('diasSalida.step')!.value)
      },
      riesgoRange: {
        original: parseFloat((this.form.get('riesgo.original') as any)?.value ?? this.valorriesgooriginal ?? 0),
        start: parseFloat(this.form.get('riesgo.start')!.value),
        end: parseFloat(this.form.get('riesgo.end')!.value),
        step: parseFloat(this.form.get('riesgo.step')!.value)
      }
    };

    console.log('[EditarParametrosCondicion] Payload enviado:', payload);

    // Emit (por compatibilidad) y cerrar pasando los datos al afterClosed()
    this.save.emit(payload);
    this.dialogRef.close(payload);
  }

  trackByIndex(index: number, _ctrl: AbstractControl): number {
    return index;
  }

  // Marcar el índice activo cuando un input obtiene el foco
  setActiveParam(index: number | null): void {
    this.activeParamIndex = index;
    try {
      this.cdr.markForCheck();
    } catch { }
  }

  // Renderizar la plantilla con el parámetro activo resaltado
  getHighlightedTemplate(): string {
    if (!this.conditionTemplate) return '';

    const escapeHtml = (s: string) => s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    let rendered = escapeHtml(this.conditionTemplate);
    if (this.activeParamIndex === null) {
      return rendered;
    }
    const token = `{{p${this.activeParamIndex}}}`;
    const escapedToken = escapeHtml(token);
    const re = new RegExp(escapedToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    rendered = rendered.replace(re, `<mark class="token-active">${escapedToken}</mark>`);
    return rendered;
  }
}